import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEK = ["S", "M", "T", "W", "T", "F", "S"];

const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Multi-date availability calendar.
 * Supports single, multiple non-contiguous and continuous range selection.
 * Colour coded by public availability status.
 */
export default function BookingCalendar({
  availability = [],
  selected = [],
  onChange,
  minDate,
  testIdPrefix = "cal",
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const boundary = minDate || today;

  const [cursor, setCursor] = useState({
    y: today.getFullYear(),
    m: today.getMonth(),
  });
  const [rangeMode, setRangeMode] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);

  const availMap = useMemo(() => {
    const map = {};
    (availability || []).forEach((a) => {
      if (a?.date && a?.status) map[a.date] = a.status;
    });
    return map;
  }, [availability]);

  const first = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const startWeek = first.getDay();
  const monthLabel = first.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  const selectedSet = new Set(selected);

  const cells = [];
  for (let i = 0; i < startWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++)
    cells.push(new Date(cursor.y, cursor.m, d));

  const statusClass = (key) => {
    const s = (availMap[key] || "").toLowerCase();
    if (s.startsWith("avail")) return "status-available";
    if (s.startsWith("limit")) return "status-limited";
    if (s.startsWith("book")) return "status-booked";
    if (s.startsWith("pend")) return "status-pending";
    return "status-open";
  };

  const toggleDate = (date) => {
    if (date < boundary) return;
    const key = fmt(date);
    if (rangeMode) {
      if (!rangeStart) {
        setRangeStart(date);
        return;
      }
      const [start, end] =
        rangeStart <= date ? [rangeStart, date] : [date, rangeStart];
      const next = new Set(selected);
      const walker = new Date(start);
      while (walker <= end) {
        next.add(fmt(walker));
        walker.setDate(walker.getDate() + 1);
      }
      onChange(Array.from(next).sort());
      setRangeStart(null);
      setRangeMode(false);
      return;
    }
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next).sort());
  };

  const stepMonth = (delta) =>
    setCursor((prev) => {
      const total = prev.y * 12 + prev.m + delta;
      return { y: Math.floor(total / 12), m: ((total % 12) + 12) % 12 };
    });

  const clearAll = () => {
    onChange([]);
    setRangeMode(false);
    setRangeStart(null);
  };

  return (
    <div className="booking-calendar" data-testid={`${testIdPrefix}-container`}>
      <div className="cal-head">
        <button
          type="button"
          onClick={() => stepMonth(-1)}
          data-testid={`${testIdPrefix}-prev`}
          aria-label="Previous month"
        >
          <ChevronLeft size={16} />
        </button>
        <strong>{monthLabel}</strong>
        <button
          type="button"
          onClick={() => stepMonth(1)}
          data-testid={`${testIdPrefix}-next`}
          aria-label="Next month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="cal-weekdays">
        {WEEK.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>

      <div className="cal-grid">
        {cells.map((d, i) => {
          if (!d) return <span key={i} className="cal-blank" />;
          const key = fmt(d);
          const past = d < boundary;
          const isRangeStart =
            rangeStart && fmt(rangeStart) === key;
          const classes = [
            "cal-cell",
            statusClass(key),
            selectedSet.has(key) ? "selected" : "",
            isRangeStart ? "range-start" : "",
            past ? "past" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              type="button"
              key={i}
              className={classes}
              onClick={() => toggleDate(d)}
              disabled={past}
              data-testid={`${testIdPrefix}-day-${key}`}
              aria-pressed={selectedSet.has(key)}
              title={availMap[key] || ""}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      <div className="cal-legend">
        <span>
          <i className="dot status-available" /> Available
        </span>
        <span>
          <i className="dot status-limited" /> Limited
        </span>
        <span>
          <i className="dot status-booked" /> Booked
        </span>
        <span>
          <i className="dot status-pending" /> Pending
        </span>
      </div>

      <div className="cal-toolbar">
        <button
          type="button"
          className={rangeMode ? "line-btn cal-range on" : "line-btn cal-range"}
          onClick={() => {
            setRangeMode((v) => !v);
            setRangeStart(null);
          }}
          data-testid={`${testIdPrefix}-range-toggle`}
        >
          {rangeMode
            ? rangeStart
              ? "PICK END DATE"
              : "PICK START DATE"
            : "SELECT DATE RANGE"}
        </button>
        {selected.length > 0 && (
          <button
            type="button"
            className="text-link"
            onClick={clearAll}
            data-testid={`${testIdPrefix}-clear`}
          >
            CLEAR
          </button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="cal-selected" data-testid={`${testIdPrefix}-selected`}>
          <p className="eyebrow">
            {selected.length} DATE{selected.length > 1 ? "S" : ""} SELECTED
          </p>
          <div className="chip-row">
            {selected.map((d) => (
              <span key={d} className="chip">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
